import { create } from 'zustand'
import { supabase, authenticateWithTimeout, fetchProfileWithTimeout } from '../lib/supabase'

interface User {
  id: string
  email?: string
  [key: string]: any
}

interface Profile {
  id: string
  email: string
  full_name: string
  phone?: string
  role: 'customer' | 'agency' | 'admin'
  [key: string]: any
}

interface AuthState {
  user: User | null
  profile: Profile | null
  loading: boolean
  initialized: boolean
  agencyInfo: any | null
  signIn: (email: string, password: string, rememberMe?: boolean) => Promise<{ error: any; user?: User; profile?: Profile }>
  signUp: (email: string, password: string, userData: any) => Promise<{ error: any; user?: User; profile?: Profile }>
  signOut: () => Promise<void>
  initialize: () => Promise<void>
  refreshProfile: () => Promise<void>
  updateProfile: (updates: Partial<Profile>) => Promise<{ error: any }>
  resetPassword: (email: string) => Promise<{ error: any }>
  updatePassword: (password: string) => Promise<{ error: any }>
  deleteAccount: () => Promise<{ error: any }>
  setUser: (user: User | null) => void
  setProfile: (profile: Profile | null) => void
  setLoading: (loading: boolean) => void
  setAgencyInfo: (agencyInfo: any | null) => void
  checkAgencyCompletion: () => Promise<boolean>
  clearAgencyCache: () => void
  deleteIncompleteAgency: () => Promise<{ error: any }>
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: false,
  initialized: false,
  agencyInfo: null,

  setUser: (user) => set({ user }),
  setProfile: (profile) => set({ profile }),
  setLoading: (loading) => set({ loading }),
  setAgencyInfo: (agencyInfo) => set({ agencyInfo }),

  checkAgencyCompletion: async () => {
    const { user } = get()
    if (!user) return false

    try {
      const { data: agencyData, error } = await supabase
        .from('agencies')
        .select('*')
        .eq('owner_id', user.id)
        .single()
      
      if (error) {
        if (error.code === 'PGRST116') { // No rows returned
          set({ agencyInfo: null })
          return false
        }
        console.error('Agency completion check error:', error)
        set({ agencyInfo: null })
        return false
      }
      
      // Check if agency is actually complete (has required fields)
      const isComplete = agencyData && 
        agencyData.name && 
        agencyData.email && 
        agencyData.phone && 
        agencyData.address && 
        agencyData.city
      
      if (isComplete) {
        // Set the user's complete agency
        set({ agencyInfo: agencyData })
        return true
      } else {
        // Agency exists but is incomplete
        console.log('Agency found but incomplete:', agencyData)
        set({ agencyInfo: null })
        return false
      }
    } catch (error) {
      console.error('Agency completion check error:', error)
      set({ agencyInfo: null })
      return false
    }
  },

  clearAgencyCache: () => {
    console.log('Clearing agency cache')
    set({ agencyInfo: null })
    // Also clear any localStorage cache if exists
    localStorage.removeItem('agency_registration_form_data')
    localStorage.removeItem('agency_registration_current_step')
  },

  deleteIncompleteAgency: async () => {
    const { user } = get()
    if (!user) return { error: 'No user found' }

    try {
      // First check if there's an incomplete agency
      const { data: agencyData, error: fetchError } = await supabase
        .from('agencies')
        .select('*')
        .eq('owner_id', user.id)
        .single()
      
      if (fetchError) {
        if (fetchError.code === 'PGRST116') {
          return { error: null } // No agency to delete
        }
        return { error: fetchError }
      }

      // Check if agency is incomplete
      const isIncomplete = !agencyData.name || 
        !agencyData.email || 
        !agencyData.phone || 
        !agencyData.address || 
        !agencyData.city

      if (isIncomplete) {
        console.log('Deleting incomplete agency:', agencyData.id)
        
        // Delete agency settings first (if any)
        await supabase
          .from('agency_settings')
          .delete()
          .eq('agency_id', agencyData.id)
        
        // Delete the incomplete agency
        const { error: deleteError } = await supabase
          .from('agencies')
          .delete()
          .eq('id', agencyData.id)
        
        if (deleteError) {
          return { error: deleteError }
        }
        
        // Clear cache
        set({ agencyInfo: null })
        localStorage.removeItem('agency_registration_form_data')
        localStorage.removeItem('agency_registration_current_step')
        
        console.log('Incomplete agency deleted successfully')
        return { error: null }
      } else {
        return { error: 'Agency is complete, cannot delete' }
      }
    } catch (error) {
      console.error('Error deleting incomplete agency:', error)
      return { error }
    }
  },

  signIn: async (email: string, password: string, rememberMe: boolean = true) => {
    console.log('[Auth] Starting sign in process for:', email)
    console.log('[Auth] Remember me setting:', rememberMe)
    set({ loading: true })
    
    try {
      // Step 1: Authenticate with timeout
      console.log('[Auth] Step 1: Authenticating user...')
      const { data, error } = await authenticateWithTimeout(email, password, 15000, rememberMe)
      
      if (error) {
        console.error('[Auth] Authentication failed:', error.message)
        set({ loading: false })
        return { error }
      }
      
      if (!data.user) {
        console.error('[Auth] No user data returned from authentication')
        set({ loading: false })
        return { error: new Error('No user data returned') }
      }
      
      console.log('[Auth] Step 2: Fetching user profile...')
      // Step 2: Fetch profile with timeout
      const { data: profileData, error: profileError } = await fetchProfileWithTimeout(data.user.id, 10000)
      
      if (profileError) {
        console.error('[Auth] Profile fetch error:', profileError)
        set({ loading: false })
        return { error: profileError }
      }
      
      const user = {
        id: data.user.id,
        email: data.user.email!,
        ...data.user
      }

      console.log('[Auth] Sign in successful for user:', user.id, 'with role:', profileData.role)
      
      set({ 
        user,
        profile: profileData,
        loading: false 
      })

      return { error: null, user, profile: profileData }
    } catch (error) {
      console.error('[Auth] Sign in process failed:', error)
      set({ loading: false })
      
      // Provide more specific error messages
      if (error.message?.includes('timeout')) {
        return { error: new Error('Authentication timed out. Please check your internet connection and try again.') }
      }
      
      return { error }
    }
  },

  signUp: async (email: string, password: string, userData: any) => {
    set({ loading: true })
    
    try {
      // Check admin settings for email verification requirement
      let emailVerificationRequired = true // Default to true for security
      
      try {
        const { data: settingsData, error: settingsError } = await supabase
          .from('admin_settings')
          .select('setting_value')
          .eq('setting_key', 'security')
          .single()
        
        if (!settingsError && settingsData?.setting_value?.emailVerification === false) {
          emailVerificationRequired = false
          console.log('[Auth] Email verification disabled by admin settings')
        } else {
          console.log('[Auth] Email verification enabled by admin settings')
        }
      } catch (error) {
        console.warn('Could not fetch admin settings, defaulting to email verification required:', error)
        // Keep default value of true for security
      }
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: emailVerificationRequired ? undefined : undefined
        }
      })
      
      if (error) {
        set({ loading: false })
        return { error }
      }

      if (data.user) {
        // Try to create profile first, then update if it exists
        let profileData = null
        let profileError = null
        
        // First, check if profile already exists by email
        const { data: existingProfileByEmail, error: selectByEmailError } = await supabase
          .from('profiles')
          .select('*')
          .eq('email', data.user.email!)
          .single()
        
        if (existingProfileByEmail) {
          // Profile already exists with this email, use it
          profileData = existingProfileByEmail
          console.log('[Auth] Using existing profile for email:', data.user.email)
        } else {
          // Try to insert new profile
          const { data: insertData, error: insertError } = await supabase
            .from('profiles')
            .insert({
              id: data.user.id,
              email: data.user.email!,
              full_name: userData.full_name,
              phone: userData.phone,
              role: userData.role || 'customer'
            })
            .select()
            .single()
          
          if (insertError && insertError.code === '23505') {
            // Profile already exists by ID, try to get it
            const { data: existingProfile, error: selectError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', data.user.id)
              .single()
            
            if (selectError) {
              profileError = selectError
            } else {
              profileData = existingProfile
            }
          } else if (insertError) {
            profileError = insertError
          } else {
            profileData = insertData
          }
        }

        if (profileError) {
          console.error('Profile creation error:', profileError)
          // Continue even if profile creation fails
        }

        const user = {
          id: data.user.id,
          email: data.user.email!,
          ...data.user
        }

        // Handle user based on email verification requirement
        if (emailVerificationRequired) {
          // Email verification required - user needs to verify email
          console.log('[Auth] Email verification required, user needs to verify email')
          set({ 
            user,
            profile: profileData,
            loading: false 
          })
          
          // Return without setting session - user needs to verify email first
          return { error: null, user: user, profile: profileData }
        } else {
          // Email verification disabled - auto sign in the user
          console.log('[Auth] Email verification disabled, auto signing in user')
          
          // Try to sign in the user immediately
          try {
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
              email: email,
              password: password
            })
            
            if (signInError) {
              console.error('[Auth] Auto sign-in after signup failed:', signInError)
              // Still return the user data even if auto sign-in fails
              set({ 
                user,
                profile: profileData,
                loading: false 
              })
              return { error: null, user: user, profile: profileData }
            } else if (signInData?.session) {
              console.log('[Auth] Auto sign-in successful after signup')
              // Update the session in our store
              set({ 
                user: {
                  id: signInData.user.id,
                  email: signInData.user.email!,
                  ...signInData.user
                },
                profile: profileData,
                loading: false 
              })
              return { error: null, user: signInData.user, profile: profileData }
            }
          } catch (error) {
            console.error('[Auth] Auto sign-in error:', error)
            set({ 
              user,
              profile: profileData,
              loading: false 
            })
            return { error: null, user: user, profile: profileData }
          }
        }
      }

      set({ loading: false })
      return { error: new Error('No user data returned') }
    } catch (error) {
      console.error('Sign up error:', error)
      set({ loading: false })
      return { error }
    }
  },

  signOut: async () => {
    set({ loading: true })

    try {
      const { error } = await supabase.auth.signOut()

      if (error) {
        console.error('Sign out error:', error)
      }
      
      set({ 
        user: null, 
        profile: null, 
        loading: false 
      })
    } catch (error) {
      console.error('Sign out error:', error)
      set({ loading: false })
    }
  },

  initialize: async () => {
    console.log('Starting background auth initialization...')
    
    try {
      // Test database connection with timeout
      console.log('Testing database connection...')
      const start = Date.now()
      
      // Create a timeout promise for database test
      const dbTestPromise = supabase
        .from('profiles')
        .select('id')
        .limit(1)
      
      const dbTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Database connection timeout')), 5000)
      )
      
      try {
        const { data, error } = await Promise.race([dbTestPromise, dbTimeoutPromise]) as any
        const duration = Date.now() - start
        
        if (error) {
          console.log(`Database connection test failed (${duration}ms):`, error.message)
          // Continue with initialization even if DB test fails
        } else {
          console.log(`Database connection test successful (${duration}ms)`)
        }
      } catch (dbError) {
        console.log('Database connection test timed out or failed:', dbError.message)
        // Continue with initialization even if DB test fails
      }
      
      // Get current session with timeout
      console.log('Getting session from Supabase...')
      
      const sessionPromise = supabase.auth.getSession()
      const sessionTimeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Session retrieval timeout')), 8000)
      )
      
      let session = null
      let sessionError = null
      
      try {
        const { data: sessionData, error: sessionErr } = await Promise.race([sessionPromise, sessionTimeoutPromise]) as any
        session = sessionData?.session
        sessionError = sessionErr
      } catch (timeoutError) {
        console.error('Session retrieval timed out:', timeoutError.message)
        // Set initialized to true even if session retrieval fails
        set({ 
          user: null, 
          profile: null, 
          initialized: true, 
          loading: false 
        })
        return
      }
      
      if (sessionError) {
        console.error('Session error:', sessionError)
        set({ initialized: true, loading: false })
        return
      }
      
      console.log('Session check complete:', !!session)
      
      if (session?.user) {
        console.log('Auth initialized with session, fetching profile...')
        
        // Fetch profile with timeout
        const profilePromise = supabase
          .from('profiles')
          .select('*')
          .eq('id', session.user.id)
          .single()
        
        const profileTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), 10000)
        )
        
        try {
          const { data: profileData, error: profileError } = await Promise.race([profilePromise, profileTimeoutPromise]) as any
          
          if (profileError) {
            console.error('Profile fetch error during initialization:', profileError)
            set({ 
              user: null, 
              profile: null, 
              initialized: true, 
              loading: false 
            })
            return
          }

          const user = {
            id: session.user.id,
            email: session.user.email!,
            ...session.user
          }

          console.log('Profile fetched during initialization:', profileData?.role)
          
          set({ 
            user,
            profile: profileData,
            initialized: true, 
            loading: false 
          })
          
          console.log('Auth initialization completed with profile')
        } catch (profileError) {
          console.error('Profile fetch timed out:', profileError.message)
          // Still mark as initialized but without profile
          set({ 
            user: null, 
            profile: null, 
            initialized: true, 
            loading: false 
          })
        }
      } else {
        console.log('No session found, setting initialized state')
        set({ 
          user: null, 
          profile: null, 
          initialized: true, 
          loading: false 
        })
      }
      
      // Set up auth state change listener (non-blocking)
      console.log('Setting up auth state change listener...')
      try {
        supabase.auth.onAuthStateChange(async (event, session) => {
          console.log('Auth state changed:', event)
          
          if (event === 'SIGNED_IN' && session?.user) {
            // Fetch profile for new session with timeout
            try {
              const profilePromise = supabase
                .from('profiles')
                .select('*')
                .eq('id', session.user.id)
                .single()
              
        const profileTimeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout on auth change')), 10000)
        )
              
              const { data: profileData, error: profileError } = await Promise.race([profilePromise, profileTimeoutPromise]) as any
              
              if (profileError) {
                console.error('Profile fetch error on sign in:', profileError)
                return
              }

              const user = {
                id: session.user.id,
                email: session.user.email!,
                ...session.user
              }

              set({ 
                user,
                profile: profileData,
                loading: false 
              })
            } catch (error) {
              console.error('Profile fetch timed out on auth change:', error.message)
            }
          } else if (event === 'SIGNED_OUT') {
            set({ 
              user: null, 
              profile: null, 
              loading: false 
            })
          }
        })
      } catch (listenerError) {
        console.error('Failed to set up auth state change listener:', listenerError)
        // Continue without listener
      }
      
      console.log('Auth initialization completed successfully')
    } catch (error) {
      console.error('Auth initialization error:', error)
      // Always mark as initialized even if there's an error
      set({ 
        user: null, 
        profile: null, 
        initialized: true, 
        loading: false 
      })
    }
  },

  refreshProfile: async () => {
    const { user } = get()
    if (!user) return

    try {
      const { data: profileData, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single()

      if (error) {
        console.error('Profile refresh error:', error)
        return
      }

      set({ profile: profileData })
    } catch (error) {
      console.error('Profile refresh error:', error)
    }
  },

  updateProfile: async (updates: Partial<Profile>) => {
    const { user } = get()
    if (!user) return { error: new Error('No user logged in') }

    try {
      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single()

      if (error) {
        return { error }
      }

      set({ profile: data })
      return { error: null }
    } catch (error) {
      return { error }
    }
  },

  resetPassword: async (email: string) => {
    try {
      // Get current language for proper redirect
      const currentLang = localStorage.getItem('kiracar-language') || 'fr'
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/callback`
      })
      
      return { error }
    } catch (error) {
      return { error }
    }
  },

  updatePassword: async (password: string) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password
      })
      
      return { error }
    } catch (error) {
      return { error }
    }
  },

  deleteAccount: async () => {
    const { user } = get()
    if (!user) return { error: new Error('No user logged in') }

    try {
      // Delete profile first
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', user.id)

      if (profileError) {
        console.error('Profile deletion error:', profileError)
        // Continue with user deletion even if profile deletion fails
      }

      // Delete user (this will be handled by database triggers if set up)
      const { error } = await supabase.auth.admin.deleteUser(user.id)
      
      if (error) {
        return { error }
      }

      set({ user: null, profile: null })
      return { error: null }
    } catch (error) {
      return { error }
    }
  }
}))

export default useAuthStore 